import dayjs from "dayjs";
import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import storageService from "../Services/storageService";
import { getApplicableTax, getCalculatedTax } from "./utils";

export const arrangePromos = (promotions) => {
  let percentagePromotion =
    promotions?.filter((pr) => pr?.discountType === "percentage") || [];

  let cashbackPromotion =
    promotions?.filter((pr) => pr?.discountType === "fixed_amount") || [];
  let freeNightPromotion =
    promotions?.filter((pr) => pr?.discountType === "nights") || [];

  let sortedPercentagePromotions = percentagePromotion?.sort(
    (a, b) => b?.discount - a?.discount
  );

  let sortedPromotions = [
    ...freeNightPromotion,
    ...sortedPercentagePromotions,
    ...cashbackPromotion,
  ];
  return sortedPromotions;
};

export const calculatePromotion = ({ price, promo, idx, dateCount }) => {
  let finalValue = 0,
    reducedPrice = 0;
  switch (promo?.discountType) {
    case "percentage":
      reducedPrice = (price * promo?.discount) / 100;
      finalValue = price - reducedPrice;
      break;
    case "fixed_amount":
      reducedPrice = promo?.discount;
      finalValue = price - reducedPrice;
      break;
    case "nights":
      if (idx >= dateCount - promo?.discount) {
        reducedPrice = price;
        finalValue = 0;
      } else {
        reducedPrice = 0;
        finalValue = price;
      }
      break;
    case null:
      reducedPrice = (price * promo?.discount) / 100;
      finalValue = price - reducedPrice;
      break;
    default:
      reducedPrice = 0;
      finalValue = price;
  }
  return { reducedPrice: Number(reducedPrice), finalValue: Number(finalValue) };
};

export function getPromotionValue(promotion, roomPrice) {
  if (promotion.discountType === "fixed_amount") {
    return parseFloat(promotion.discount);
  } else if (
    promotion.discountType === "percentage" ||
    promotion.discountType === null
  ) {
    return (parseFloat(promotion.discount) / 100) * roomPrice;
  } else if (promotion.discountType === "nights") {
    return roomPrice;
  }
  return 0;
}

export const getApplicablePromotion = ({
  arrangedPromos,
  tempPrice,
  rp,
  checkInDate,
}) => {
  if (arrangedPromos?.length < 1) {
    return null;
  }

  let sortedPromotions = [...arrangedPromos].sort((a, b) => {
    const aValue = getPromotionValue(a, tempPrice);
    const bValue = getPromotionValue(b, tempPrice);
    return bValue - aValue;
  });

  let appliedPromo = null;
  for (let i = 0; i < sortedPromotions?.length; i++) {
    if (!appliedPromo) {
      let promo = sortedPromotions[i];
      let customArray = promo?.PromotionsExtraConfigs || [];

      if (
        !isCheckInDateValid(
          promo?.stayStartDate,
          promo?.stayEndDate,
          checkInDate
        )
      ) {
        continue;
      }

      if (!isCheckInDateValid(promo?.startDate, promo?.endDate, new Date())) {
        continue;
      }

      switch (promo?.type) {
        case "bulk":
          if (promo?.discountType === null) {
            appliedPromo = null;
          } else if (tempPrice >= promo?.cutoffAmount) {
            appliedPromo = promo;
            if (customArray?.length > 0) {
              appliedPromo = checkIfApplicable({
                tempPrice,
                rp,
                checkInDate,
                customArray,
                promo,
              });
            }
          } else {
            appliedPromo = null;
          }
          break;

        case "custom":
          appliedPromo = promo;

          if (
            (promo?.cutoffAmount && tempPrice >= promo?.cutoffAmount) ||
            promo?.cutoffAmount === null
          ) {
            if (customArray?.length > 0) {
              appliedPromo = checkIfApplicable({
                tempPrice,
                rp,
                checkInDate,
                customArray,
                promo,
              });
            }
          } else {
            appliedPromo = null;
          }
          break;
        case "coupon":
          appliedPromo = promo;
          if (
            tempPrice >= promo?.cutoffAmount ||
            promo?.cutoffAmount === null
          ) {
            if (customArray?.length > 0) {
              appliedPromo = checkIfApplicable({
                tempPrice,
                rp,
                checkInDate,
                customArray,
                promo,
              });
            }
          } else {
            appliedPromo = null;
          }
          break;

        case "day":
          appliedPromo = promo;
          if (customArray?.length > 0) {
            appliedPromo = checkIfApplicable({
              tempPrice,
              rp,
              checkInDate,
              customArray,
              promo,
            });
          }
          break;
        case "basic":
          appliedPromo = promo;
          if (customArray?.length > 0) {
            appliedPromo = checkIfApplicable({
              tempPrice,
              rp,
              checkInDate,
              customArray,
              promo,
            });
          }
          break;
        default:
          appliedPromo = promo;
          if (customArray?.length > 0) {
            appliedPromo = checkIfApplicable({
              tempPrice,
              rp,
              checkInDate,
              customArray,
              promo,
            });
          }
          break;
      }
    }
  }

  return appliedPromo;
};

export function isCheckInDateValid(startDate, endDate, checkInDate) {
  // Convert input dates to Date objects
  let start = startDate ? new Date(startDate) : null;
  let end = endDate ? new Date(endDate) : null;
  let checkIn = new Date(checkInDate);

  // Check if check-in date is between start and end dates
  if (start && end) {
    return checkIn >= start && checkIn <= end;
  } else if (start) {
    return checkIn >= start;
  } else if (end) {
    return checkIn <= end;
  } else {
    return true; // If both startDate and endDate are null, any checkInDate is valid
  }
}

export const getApplicablePromotionRoom = ({
  arrangedPromos,
  tempPrice,
  rp,
  checkInDate,
}) => {
  if (arrangedPromos?.length < 1) {
    return null;
  }

  let sortedPromotions = [...arrangedPromos].sort((a, b) => {
    const aValue = getPromotionValue(a, tempPrice);
    const bValue = getPromotionValue(b, tempPrice);
    return bValue - aValue;
  });

  let appliedPromo = null;
  for (let i = 0; i < sortedPromotions?.length; i++) {
    if (!appliedPromo) {
      let promo = sortedPromotions[i];
      let customArray = promo?.PromotionsExtraConfigs || [];
      if (
        !isCheckInDateValid(
          promo?.stayStartDate,
          promo?.stayEndDate,
          checkInDate
        )
      ) {
        continue;
      }

      if (!isCheckInDateValid(promo?.startDate, promo?.endDate, new Date())) {
        continue;
      }

      switch (promo?.type) {
        case "bulk":
          if (
            promo?.discountType === null &&
            tempPrice >= promo?.cutoffAmount
          ) {
            appliedPromo = promo;
          } else if (tempPrice >= promo?.cutoffAmount) {
            appliedPromo = promo;
            if (customArray?.length > 0) {
              appliedPromo = checkIfApplicable({
                tempPrice,
                rp,
                checkInDate,
                customArray,
                promo,
              });
            }
          } else {
            appliedPromo = null;
          }
          break;

        case "custom":
          appliedPromo = promo;

          if (
            tempPrice >= promo?.cutoffAmount ||
            promo?.cutoffAmount === null
          ) {
            if (customArray?.length > 0) {
              appliedPromo = checkIfApplicable({
                tempPrice,
                rp,
                checkInDate,
                customArray,
                promo,
              });
            }
          } else {
            appliedPromo = null;
          }
          break;
        case "day":
          appliedPromo = promo;
          if (customArray?.length > 0) {
            appliedPromo = checkIfApplicable({
              tempPrice,
              rp,
              checkInDate,
              customArray,
              promo,
            });
          }
          break;
        case "basic":
          appliedPromo = promo;
          if (customArray?.length > 0) {
            appliedPromo = checkIfApplicable({
              tempPrice,
              rp,
              checkInDate,
              customArray,
              promo,
            });
          }
          break;
        default:
          appliedPromo = promo;
          if (customArray?.length > 0) {
            appliedPromo = checkIfApplicable({
              tempPrice,
              rp,
              checkInDate,
              customArray,
              promo,
            });
          }
          break;
      }
    }
  }
  return appliedPromo;
};

export const checkIfApplicable = ({ rp, checkInDate, customArray, promo }) => {
  let pro = promo;

  const hasRateplanCheck = !!customArray.find(
    (custom) => custom?.type === "rateplan_id"
  );
  for (let j = 0; j < customArray?.length; j++) {
    switch (customArray[j]?.type) {
      case "blackout_date":
        if (customArray[j]?.value?.includes(checkInDate)) {
          return null;
        } else {
          pro = promo;
        }
        break;
      case "day_of_week":
        if (
          customArray[j]?.value?.includes(
            dayjs(checkInDate).format("dddd").toLowerCase()
          )
        ) {
          pro = promo;
        } else {
          return null;
        }
        break;
      case "room_id":
        if (!customArray[j]?.value?.includes(String(rp?.roomId))) {
          if (!hasRateplanCheck) {
            return null;
          }
        } else {
          pro = promo;
          return pro;
        }
        break;
      case "rateplan_id":
        if (customArray[j]?.value?.includes(String(rp?.rpObject?.id))) {
          pro = promo;
        } else {
          return null;
        }
        break;
    }
  }

  return pro;
};

const usePriceCalculator = () => {
  const {
    selectedRatePlans = [],
    hotelData = {},
    dateRange,
    dateCount,
    selectedHotelAddons = [],
    promotions = [],
    taxes = [],
    couponCode = {},
  } = useSelector((state) => state.hotelInfo);

  const { HotelRooms = [] } = hotelData;

  const [rpPrice, setRPPrice] = useState(0);
  const [selectedRooms, setSelectedRooms] = useState([]);
  const [rpTotalGuest, setRPTotalGuest] = useState(0);
  const [adultCount, setAdultCount] = useState(0);
  const [childCount, setChildCount] = useState(0);
  const [infantCount, setInfantCount] = useState(0);
  const [rpTax, setRPTax] = useState(0);
  const [totalRoomCost, setTotalRoomCost] = useState(0);
  const [totalAddonCost, setTotalAddonCost] = useState(0);
  const [appliedDiscount, setAppliedDiscount] = useState({ discount: 0 });
  const [priceAfterDiscount, setPriceAfterDiscount] = useState(0);
  const [arrangedPromos, setArrangedPromos] = useState([]);
  const [rpPriceWODiscount, setRPPriceWODiscount] = useState(0);
  const [bulkDiscount, setBulkDiscount] = useState({});
  const [taxObj, setTaxObj] = useState({});
  const [couponValue, setCouponValue] = useState(0);

  useEffect(() => {
    const arrange = () => {
      let ar = arrangePromos(promotions);
      setArrangedPromos(ar);
    };
    promotions?.length > 0 && arrange();
  }, [promotions]);

  const getLowestPrice = (
    adultCount,
    childCount,
    infantCount,
    minorObj,
    pricing
  ) => {
    let totalGuest = adultCount + childCount + infantCount;

    let adultPricing =
      pricing?.filter((pr) => pr?.adultCount === adultCount)?.[0]
        ?.priceForPax || [];

    let childPricing = minorObj?.children;
    let infantPricng = minorObj?.infants;

    let dates = adultPricing?.map((date) => date?.checkInDate) || [];

    let rpArr = [];

    dates.forEach((date) => {
      let sum = 0,
        tax = 0;

      // calculate adult price
      sum += adultPricing?.filter((dt) => dt?.checkInDate === date)?.[0]
        ?.priceBeforeTax;

      if (childCount > 0) {
        let datePrice = childPricing?.filter(
          (dt) => dt?.checkInDate === date
        )[0]?.priceBeforeTax;
        let childPrice = 0;
        for (let i = 0; i < childCount; i++) {
          childPrice += datePrice;
        }

        if (pricing?.some((pr) => pr?.adultCount === adultCount + childCount)) {
          let extraAdultComparePrice =
            pricing?.filter(
              (pr) => pr?.adultCount === adultCount + childCount
            )?.[0]?.priceForPax || [];
          let priceForThis = extraAdultComparePrice?.filter(
            (pr) => pr?.checkInDate === date
          )?.[0]?.priceBeforeTax;
          sum = Math.min(priceForThis, sum + childPrice);
        } else {
          sum += childPrice;
        }
      }
      if (infantCount > 0) {
        // compare the price with child and swap it with infant;
        let childDatePrice = childPricing?.filter(
          (dt) => dt?.checkInDate === date
        )[0]?.priceBeforeTax;

        let infantDatePrice = infantPricng?.filter(
          (dt) => dt?.checkInDate === date
        )[0]?.priceBeforeTax;

        let infantPrice = Math.min(childDatePrice, infantDatePrice);
        let price = 0;
        for (let i = 0; i < infantCount; i++) {
          price += infantPrice;
        }

        if (
          pricing?.some(
            (pr) => pr?.adultCount === adultCount + infantCount + childCount
          )
        ) {
          let extraAdultComparePrice =
            pricing?.filter(
              (pr) => pr?.adultCount === adultCount + infantCount + childCount
            )?.[0]?.priceForPax || [];
          let priceForThis = extraAdultComparePrice?.filter(
            (pr) => pr?.checkInDate === date
          )?.[0]?.priceBeforeTax;
          sum = Math.min(priceForThis, sum + price);
        } else {
          sum += price;
        }
      }
      let obj = {
        checkInDate: date,
        priceBeforeTax: sum,
      };
      rpArr.push(obj);
    });

    return rpArr;
  };

  const checkForApplicableBulk = () => {
    if (!rpPriceWODiscount) {
      return;
    }
    if (promotions?.length === 0 || !promotions) {
      return;
    }

    let filterArrays =
      promotions?.filter(
        (pr) => pr?.discountType === null && pr?.type === "bulk"
      ) || [];

    if (filterArrays?.length === 0 || !filterArrays) {
      return;
    }

    let sortedPromotions = [...filterArrays].sort((a, b) => {
      const aValue = getPromotionValue(a, rpPriceWODiscount);
      const bValue = getPromotionValue(b, rpPriceWODiscount);
      return bValue - aValue;
    });

    for (let i = 0; i < sortedPromotions.length; i++) {
      let pr = sortedPromotions[i];
      if (rpPriceWODiscount >= Number(pr?.cutoffAmount)) {
        let discount =
          rpPriceWODiscount - (rpPriceWODiscount * pr?.discount) / 100;
        if (discount < rpPrice + rpTax) {
          let rms = [];
          let tax = 0;
          let rSum = 0,
            reduced = 0;
          let arr = [];
          selectedRooms.forEach((rm) => {
            let objArr = [];
            rm?.onedayObj?.forEach((od) => {
              let sum = od?.noDiscPrice;
              rSum += sum;
              let reds = (sum * pr?.discount) / 100;
              reduced += reds;
              objArr.push({
                ...od,
                finalValue: sum - reds,
                reducedPrice: reds,
              });
            });
            arr.push({ ...rm, onedayObj: objArr });
            setBulkDiscount(pr);
          });
          setSelectedRooms(arr);
          setRPTax(tax);
          setTotalRoomCost(rSum - reduced);
          setAppliedDiscount({ discount: rpPriceWODiscount - discount });
          setPriceAfterDiscount(discount);
          return null;
        } else if (discount === rpPrice) {
          return null;
        } else {
          setBulkDiscount({});
          return null;
        }
      }
    }
  };
  useEffect(() => {
    checkForApplicableBulk();
  }, [rpPrice, rpPriceWODiscount, appliedDiscount.discount, promotions]);

  useEffect(() => {
    const calculatePrice = () => {
      if (!selectedRatePlans) {
        return;
      }

      let totalSum = 0;
      let noDiscPrice = 0;
      let guestCount = 0;
      let totalPriceReduced = 0;
      let arr = [];
      let totalTax = 0;
      let roomCost = 0;
      let adnCost = 0;
      let temAdult = 0,
        temChild = 0,
        couponDiscValue = 0,
        temInfant = 0;

      for (let rp of selectedRatePlans) {
        let sum = 0;
        let tax = 0;
        let priceReduced = 0;
        let promosApplied = [];
        let sumObj = [];
        let onedayObj = [];

        const room = HotelRooms?.filter((rm) => rm?.id === rp?.roomId)?.[0];

        temAdult += rp?.sAdult;
        temChild += rp?.sChild;
        temInfant += rp?.sInfant;

        const {
          pricing = [],
          pricingAdditionalGuests = {},
          ratePlans = {},
        } = room;

        guestCount += rp?.sAdult + rp?.sChild + rp?.sInfant;

        // Child and Infant Price Calculation
        const minorObj = pricingAdditionalGuests[rp?.code];

        let allGuestsObj =
          pricing?.filter(
            (r) =>
              r?.ratePlanCode === rp?.code &&
              r?.adultCount === rp?.sAdult &&
              r?.childCount === rp?.sChild &&
              r?.infantCount === rp?.sInfant
          ) || [];

        sumObj = getLowestPrice(
          rp?.sAdult,
          rp?.sChild,
          rp?.sInfant,
          minorObj,
          pricing?.filter((r) => r?.ratePlanCode === rp?.code)
        );

        if (allGuestsObj?.length > 0) {
          sumObj = allGuestsObj?.flatMap((p) => p?.priceForPax);
        }

        sumObj.forEach((obj, idx) => {
          let tempPrice = obj?.priceBeforeTax;
          let checkInDate = obj?.checkInDate;
          let couponPromo = {};
          let parr = [];

          let promo = getApplicablePromotion({
            arrangedPromos,
            rp,
            tempPrice,
            checkInDate,
          });

          noDiscPrice += tempPrice;

          let { finalValue, reducedPrice } = calculatePromotion({
            price: tempPrice,
            promo,
            idx,
            dateCount,
          });

          if (couponCode?.id) {
            couponPromo = getApplicablePromotion({
              arrangedPromos: [couponCode],
              rp,
              tempPrice: couponPromo?.isStack ? finalValue : tempPrice,
              checkInDate,
            });
            if (couponPromo) {
              let couponCalc = calculatePromotion({
                price: couponPromo?.isStack ? finalValue : tempPrice,
                promo: couponPromo,
                idx,
                dateCount,
              });
              couponDiscValue += couponCalc.reducedPrice;
              finalValue = couponCalc.finalValue || finalValue;
              if (couponPromo?.isStack) {
                reducedPrice += couponCalc.reducedPrice;
              } else {
                reducedPrice = couponCalc.reducedPrice;
              }
            }
          }

          if (promo || couponPromo?.id) {
            if (promo) {
              parr.push(promo);
            }
            if (couponPromo && couponPromo?.id) {
              if (couponPromo?.isStack) {
                parr.push(couponPromo);
              } else {
                parr = [couponPromo];
              }
            }
            promosApplied.push({ promo: parr, checkInDate, reducedPrice });
          }

          priceReduced += reducedPrice;

          const taxObj = getCalculatedTax({
            taxes: taxes,
            rooms: [{ id: room?.id, amount: finalValue }],
            isInclusive: hotelData?.isTaxIncluded,
            noOfNights: 1,
          });

          sum += finalValue;
          tax += taxObj.totalRoomTax;

          let oneobj = {
            noDiscPrice: obj?.priceBeforeTax,
            checkInDate: obj?.checkInDate,
            finalValue,
            reducedPrice,
            tax: taxObj.totalRoomTax,
            roomId: room?.id,
          };
          onedayObj.push(oneobj);
        });

        let rmObj = {
          ...rp,
          sum,
          tax,
          priceReduced,
          promosApplied: [...new Set(promosApplied)],
          room,
          onedayObj,
        };
        arr.push(rmObj);
        totalSum += rmObj?.sum;
        totalTax += rmObj?.tax;
        totalPriceReduced += rmObj?.priceReduced;
      }
      setAdultCount(temAdult);
      setChildCount(temChild);
      setInfantCount(temInfant);

      selectedHotelAddons?.forEach((adn) => {
        if (adn.Addon.multiplier === "per_day") {
          adnCost += parseFloat(adn.price) * dateCount;
        } else {
          adnCost += parseFloat(adn.price);
        }
      });

      // if (hotelData?.currency !== "INR") {
      //   totalSum += totalTax;
      //   totalTax = 0;
      // }

      storageService.set("totalPrice", totalSum + adnCost);
      setRPPrice(totalSum + adnCost);
      setRPPriceWODiscount(noDiscPrice + adnCost);
      setRPTax(totalTax);
      setTotalRoomCost(totalSum);
      setCouponValue(couponDiscValue);
      setTotalAddonCost(adnCost);
      setRPTotalGuest(guestCount);
      setAppliedDiscount({ discount: totalPriceReduced });
      setPriceAfterDiscount(totalSum);
      setSelectedRooms(arr);
    };
    calculatePrice();
  }, [selectedRatePlans, selectedHotelAddons, arrangedPromos, couponCode]);

  useEffect(() => {
    const calculateTax = () => {
      let arr = [...selectedRooms];
      let newArr = [];
      arr.forEach((rm) => {
        newArr = [
          ...newArr,
          ...rm?.onedayObj?.map((rm) => ({
            id: rm?.roomId,
            amount: rm?.finalValue,
          })),
        ];
      });
      let txObj = getCalculatedTax({
        rooms: newArr,
        taxes: taxes,
        isInclusive: hotelData?.isTaxIncluded,
        noOfNights: dateCount,
      });

      setTaxObj(txObj);
    };

    selectedRooms.length > 0 && calculateTax();
  }, [selectedRooms, taxes, bulkDiscount]);

  return {
    rpPrice,
    selectedRooms,
    couponValue,
    rpTotalGuest,
    rpTax,
    totalRoomCost,
    priceAfterDiscount,
    taxObj,
    bulkDiscount,
    totalAddonCost,
    appliedDiscount,
    adultCount,
    childCount,
    infantCount,
  };
};

export default usePriceCalculator;
